import { Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ActivatedRoute, Params, RouterModule } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';

import { distinctUntilChanged, OperatorFunction } from 'rxjs';
import { CookieConsentService } from '@metallgehalt/shared';
import { BruttoNettoCalculatorService, TaxDataService } from '@metallgehalt/brutto-netto-calculator';

import { TvoedSalaryService } from '../../services/tvoed-salary/tvoed-salary.service';
import { TvoedCalculatorService } from '../../services/tvoed-calculator/tvoed-calculator.service';
import { TvoedLabourAgreement } from '../../models/tvoed-labour-agreement.model';
import { TvoedData } from '../../models/tvoed-data.model';
import { MonthlyChartComponent } from '../monthly-chart/monthly-chart.component';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatOptionModule } from '@angular/material/core';

@Component({
  selector: 'tvoed-rechner-calculator',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    MatSnackBarModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    ClipboardModule,
    MatCheckboxModule,
    MatExpansionModule,
    MatIconModule,
    MatOptionModule,
    MonthlyChartComponent
  ],
  templateUrl: './calculator.component.html',
  styleUrls: ['./calculator.component.scss'],
  providers: [BruttoNettoCalculatorService]
})
export class CalculatorComponent implements OnInit {
  private bruttoNettoService = inject(BruttoNettoCalculatorService);
  private bruttoNettoTaxDataService = inject(TaxDataService);

  hasBruttoNettoSettings = signal(false);
  bruttoNettoReady = signal(false);

  estimatedNettoMonthly = computed(() => {
    if (!this.hasBruttoNettoSettings() || !this.bruttoNettoReady()) return 0;
    return this.bruttoNettoService.nettoMonthly();
  });

  estimatedNettoYearly = computed(() => {
    if (!this.hasBruttoNettoSettings() || !this.bruttoNettoReady()) return 0;
    return this.bruttoNettoService.nettoYearly();
  });

  bruttoMonthlyRounded = computed(() => Math.round(this.calcService.totalMonthlySalary() * 100) / 100);

  private fb = inject(FormBuilder);
  private salaryService = inject(TvoedSalaryService);
  private route = inject(ActivatedRoute);
  private snackBar = inject(MatSnackBar);
  private titleService = inject(Title);
  private metaService = inject(Meta);
  cookieConsentService = inject(CookieConsentService);
  calcService = inject(TvoedCalculatorService);

  form: FormGroup = this.fb.group({
    labourAgreementId: [null, Validators.required],
    subTable: ['', Validators.required],
    payGrade: ['', Validators.required],
    payGradeStep: ['', Validators.required],
    weeklyWorkingHours: [39, [Validators.min(30), Validators.max(45), Validators.required]],
    weeklyAdditionalWorkingHours: [0, [Validators.min(-20), Validators.max(20), Validators.required]],
    additionalExtraPayment: 0,
    zulagenSchicht: false,
    zulagenNacht: false,
    zulagenWochenende: false,
    zulagenUeberstunden: false,
    zulagenFunktion: false,
    leistungsentgeltAmount: [0, [Validators.min(0), Validators.max(50000)]],
    rememberInputs: false
  });

  loading = false;
  labourAgreements = signal<TvoedLabourAgreement[]>([]);
  selectedLabourAgreement = signal<TvoedLabourAgreement | undefined>(undefined);

  ngOnInit() {
    this.titleService.setTitle(`Gehaltsrechner für den TVöD (Tarifvertrag öffentlicher Dienst)`);
    this.metaService.updateTag({
      name: 'description',
      content: 'TVöD Gehaltsrechner: Berechne dein Gehalt im öffentlichen Dienst inklusive Jahressonderzahlung, Leistungsentgelt und Zulagen'
    });
    this.metaService.updateTag({
      name: 'keywords',
      content:
        'TVöD, Tarifvertrag öffentlicher Dienst, TVöD Rechner, TVöD Gehaltsrechner, Gehaltsberechnung, Jahressonderzahlung, Leistungsentgelt, VKA, Bund, SuE, Pflege, Entgeltgruppe, Stufe, öffentlicher Dienst Gehalt'
    });

    this.initBruttoNettoIntegration();

    // When form changes, update the store
    this.form.valueChanges.pipe(this.distinctUntilChangedExcluding(['subTable'])).subscribe(formValues => {
      this.calcService.updateState({ ...formValues });

      if (formValues.rememberInputs) {
        localStorage.setItem('tvoedParameters', JSON.stringify(this.form.getRawValue()));
      }
    });

    this.form.get('subTable')?.valueChanges.subscribe(subTable => {
      this.calcService.updateState({ subTable });
      // Auto-set weekly working hours based on sub-table
      const defaultHours = this.calcService.getDefaultWorkingHours();
      this.form.patchValue({ weeklyWorkingHours: defaultHours });
    });

    // Fetch labour agreements
    this.salaryService.getLabourAgreements().subscribe(data => {
      this.labourAgreements.set(data);
      const stored = JSON.parse(localStorage.getItem('tvoedParameters') ?? 'null');
      if (stored) {
        this.form.patchValue(stored);
      }
    });

    // Optionally store inputs in localStorage
    this.form.get('rememberInputs')?.valueChanges.subscribe(remember => {
      if (remember) {
        localStorage.setItem('tvoedParameters', JSON.stringify(this.form.getRawValue()));
      } else {
        localStorage.removeItem('tvoedParameters');
      }
    });

    // Listen for query parameters and patch the form
    this.route.queryParams.subscribe(params => {
      if (params['labourAgreementId']) {
        const parsedParams = this.parseRoutingParams(params);
        this.form.patchValue(parsedParams, { emitEvent: false });
        this.calcService.updateState({ ...parsedParams });
      }
    });
  }

  /** Keep brutto-netto calculator in sync with salary calculator's total monthly salary */
  syncBruttoNetto = effect(() => {
    const totalMonthly = this.calcService.totalMonthlySalary();
    if (this.hasBruttoNettoSettings() && totalMonthly > 0) {
      this.bruttoNettoService.updateState({ bruttoMonthly: totalMonthly });
    }
  });

  fetchTvoedDataAndExtraPayments = effect(() => {
    if (this.labourAgreements().length && this.calcService.state.labourAgreementId()) {
      const labourAgreementId = this.calcService.state.labourAgreementId();
      this.selectedLabourAgreement.set(this.labourAgreements().find(la => `${la.id}` === `${labourAgreementId}`));

      const selected = this.selectedLabourAgreement();
      if (selected) {
        this.salaryService.getTvoedData(selected.tvoed).subscribe((tvoedData: TvoedData) => {
          this.salaryService.getExtraPayments(selected.extraPayments).subscribe(extraPaymentData => {
            this.calcService.updateState({ tvoedData, extraPaymentData });
          });
        });
      }
    }
  });

  copied(copied: boolean) {
    if (copied) {
      this.snackBar.open('Link in die Zwischenablage kopiert!', undefined, {
        duration: 2500,
        verticalPosition: 'top'
      });
    }
  }

  private initBruttoNettoIntegration() {
    const stored = localStorage.getItem('bruttoNettoParameters');
    if (!stored) return;

    this.hasBruttoNettoSettings.set(true);
    const params = JSON.parse(stored);
    const year = params.year ?? new Date().getFullYear();

    this.bruttoNettoService.updateState({
      steuerklasse: params.steuerklasse ?? 3,
      kirchensteuer: params.kirchensteuer ?? false,
      bundesland: params.bundesland ?? 'nordrhein-westfalen',
      kinderlos: params.kinderlos ?? true,
      anzahlKinder: params.anzahlKinder ?? 0,
      kinderfreibetraege: params.kinderfreibetraege ?? 0,
      kvZusatzbeitrag: (params.kvZusatzbeitrag ?? 2.9) / 100,
      year: year,
      gesetzlichRentenversichert: params.gesetzlichRentenversichert ?? true,
      gesetzlichArbeitslosenversichert: params.gesetzlichArbeitslosenversichert ?? true,
      krankenversicherungsArt: params.krankenversicherungsArt ?? 'gesetzlich',
      privatKvBeitrag: params.privatKvBeitrag ?? 0,
      geldwerterVorteil: params.geldwerterVorteil ?? 0,
      bruttoMonthly: 0
    });

    this.bruttoNettoTaxDataService.getTaxParams(year).subscribe(taxParams => {
      this.bruttoNettoService.updateState({ taxParams });
      this.bruttoNettoReady.set(true);
    });
  }

  parseRoutingParams(routingParams: Params) {
    return {
      ...routingParams,
      weeklyWorkingHours: parseFloat(routingParams['weeklyWorkingHours']),
      weeklyAdditionalWorkingHours: parseFloat(routingParams['weeklyAdditionalWorkingHours']),
      additionalExtraPayment: routingParams['additionalExtraPayment'] ? parseFloat(routingParams['additionalExtraPayment']) : 0,
      zulagenSchicht: JSON.parse(routingParams['zulagenSchicht'] ?? 'false'),
      zulagenNacht: JSON.parse(routingParams['zulagenNacht'] ?? 'false'),
      zulagenWochenende: JSON.parse(routingParams['zulagenWochenende'] ?? 'false'),
      zulagenUeberstunden: JSON.parse(routingParams['zulagenUeberstunden'] ?? 'false'),
      zulagenFunktion: JSON.parse(routingParams['zulagenFunktion'] ?? 'false'),
      leistungsentgeltAmount: routingParams['leistungsentgeltAmount'] ? parseFloat(routingParams['leistungsentgeltAmount']) : 0
    };
  }

  /**
   * Erzeugt einen Operator, der zwei Objekte vergleicht,
   * dabei aber die angegebenen Felder ausschließt.
   */
  distinctUntilChangedExcluding<T extends Record<string, unknown>>(fields: string[]): OperatorFunction<T, T> {
    return distinctUntilChanged((prev: T, curr: T) => {
      const filteredPrev = this.removeFields(prev, fields);
      const filteredCurr = this.removeFields(curr, fields);
      return JSON.stringify(filteredPrev) === JSON.stringify(filteredCurr);
    });
  }

  private removeFields<T extends Record<string, unknown>>(obj: T, fields: string[]): Partial<T> {
    const clone = { ...obj };
    fields.forEach(f => delete clone[f as keyof T]);
    return clone;
  }
}
